import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import apiClient from "@/api/client";
import axios from "axios";

const AuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Refresh token every 20 minutes to prevent expiration
const TOKEN_REFRESH_INTERVAL = 20 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshToken = useCallback(async () => {
    try {
      await apiClient.post("/auth/refresh");
    } catch (err) {
      // Silent - cookie might still be valid for non-refresh endpoints
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      // Direct call (no interceptor) to avoid redirect loops on /login
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch (err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { 
    checkAuth(); 
    
    // Set up token refresh interval
    const refreshInterval = setInterval(refreshToken, TOKEN_REFRESH_INTERVAL);
    
    // Refresh token when page becomes visible (user returns to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshToken();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkAuth, refreshToken]);

  const login = async (email, password) => {
    const res = await apiClient.post("/auth/login", { email, password });
    // Cookie is set automatically by the browser from Set-Cookie header
    setUser(res.data.user);
    return res.data;
  };

  const logout = async () => {
    try { await apiClient.post("/auth/logout"); } catch { /* cookie cleared server-side */ }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      loading,
      isAdmin: user?.role === "ADMIN",
      isResponsable: user?.role === "RESPONSABLE",
      isCoach: user?.role === "COACH",
      hasCrmAccess: user?.role === "ADMIN" || user?.role === "RESPONSABLE",
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
